import { gql } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { RiExternalLinkFill } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { formatIsoDateToOrgDate } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import { FORM_TEMPLATE_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { useEditFormTemplateContext } from "./context";
import client from "./FormTemplateClient";

const FormSchema = z.object({
  formTemplateId: z
    .string({ message: "This field is required" })
    .min(1, { message: "This field is required" }),
});
type FormType = z.infer<typeof FormSchema>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  targetFormTemplateId: uuid;
};
export const ImportFormTemplateDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  targetFormTemplateId,
}) => {
  const organization = useOrganization();
  const { remoteData } = useRemoteDataQuery<
    GQL.GetFormTemplateForImport,
    GQL.GetFormTemplateForImportVariables
  >(
    gql`
      query GetFormTemplateForImport(
        $organizationId: uuid!
        $targetFormTemplateId: uuid!
      ) {
        form_template(
          where: {
            id: { _neq: $targetFormTemplateId }
            enrollment_period: { organization_id: { _eq: $organizationId } }
            deleted_at: { _is_null: true }
            schema_version: { _eq: "3.0.0" }
          }
        ) {
          id
          name
          enrollment_period {
            id
            name
          }
          updated_at
        }
      }
    `,
    {
      variables: {
        organizationId: organization.toNullable()?.id ?? "",
        targetFormTemplateId,
      },
      skip: !organization.hasData(),
    }
  );

  const formTemplateContext = useEditFormTemplateContext();

  const toast = useAvelaToast();

  const importFromOther = client.useImportFromOther();

  const importFormTemplate = React.useCallback(
    async (values: FormType) => {
      try {
        const sourceFormTemplate = remoteData
          .toNullable()
          ?.form_template.find((ft) => ft.id === values.formTemplateId);

        const result = await importFromOther(
          targetFormTemplateId,
          values.formTemplateId
        );

        if (!result.response.ok || !result.data?.data) {
          toast.genericError();
          return;
        }
        const { schema, errors } = result.data.data;

        if (schema.schemaVersion !== "3.0.0") {
          toast.error({ title: "Invalid schema version" });
          return;
        }
        formTemplateContext.dispatch({
          type: "SetFormTemplate",
          formTemplate: schema,
        });
        formTemplateContext.dispatch({
          type: "SetImportErrors",
          errors: errors,
        });
        onClose();
        toast({
          title: "Imported from existing",
          description: sourceFormTemplate
            ? `Content imported from ${sourceFormTemplate.name}`
            : undefined,
        });
      } catch (error: unknown) {
        console.error(error);
        toast.genericError();
      }
    },
    [
      formTemplateContext,
      importFromOther,
      onClose,
      remoteData,
      targetFormTemplateId,
      toast,
    ]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <Formik<FormType>
        initialValues={{ formTemplateId: "" }}
        onSubmit={importFormTemplate}
        validate={validateWithZod(FormSchema)}
      >
        {(formik) => (
          <ModalContent as={Form} noValidate>
            <ModalHeader>Import form</ModalHeader>
            <ModalBody>
              <Flex direction="column" gap="4">
                <Text>
                  Select a form to import items from it.{" "}
                  <Box fontWeight="bold" as="span">
                    The import will replace any existing content.
                  </Box>
                </Text>
                <RemoteDataView
                  remoteData={remoteData}
                  error={(error) => <GenericError error={error} />}
                  loading={<Skeleton width="100%" height="2rem" />}
                >
                  {(data) => (
                    <FormikSearchSelect
                      name="formTemplateId"
                      label="Form to import form"
                      items={data.form_template.map((ft) => ({
                        key: ft.id,
                        label: ft.name,
                        action: (
                          <Tooltip label={`Open ${ft.name} in new tab`}>
                            <Link
                              aria-label={`Open ${ft.name} in new tab`}
                              target="_blank"
                              onClick={(event) => event.stopPropagation()}
                              href={organization
                                .map((org) =>
                                  Url.OrgAdmin.FormTemplates.edit(
                                    org,
                                    ft.enrollment_period.id,
                                    ft.id,
                                    FORM_TEMPLATE_TAB_HASH.CONTENT
                                  )
                                )
                                .withDefault("#")}
                            >
                              <Icon as={RiExternalLinkFill} />
                            </Link>
                          </Tooltip>
                        ),
                        description: (
                          <>
                            <Text>{ft.enrollment_period.name}</Text>
                            <Text>
                              Last modified on{" "}
                              {formatIsoDateToOrgDate(
                                ft.updated_at,
                                organization
                              )}
                            </Text>
                          </>
                        ),
                      }))}
                    />
                  )}
                </RemoteDataView>
              </Flex>
            </ModalBody>
            <ModalFooter gap="3">
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={formik.isSubmitting}>
                Next
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  );
};
